import { COMPONENT_STATES, COMPONENT_TYPE } from 'common/constants';
import { UNIFORM_PROPS } from 'common/constants/component-props';
import { componentWalkFull } from 'common/utils/blocks/component-walk-full';
import type { BBModel, BBStates, BBUiConfig, ComponentTypes, StoryVersionType } from 'types/story';

/**
 * When an instance becomes an independent action boundary:
 * - `always`: every instance can handle its own click, keyboard action, or gesture;
 * - `linked`: the instance becomes actionable only after its link resolves to a destination;
 * - `passive`: the block itself has no action, so the event may reach an actionable ancestor.
 */
type ActivationOwnership = 'always' | 'linked' | 'passive';

type BuildingBlockCapabilities = {
	/** Shows link settings and allows this type to resolve the persisted LinkFields as a destination. */
	link: boolean;

	/** Determines whether `isIndependentAction` treats an instance as an action boundary. */
	activation: ActivationOwnership;

	/**
	 * The rendered content may contain interactions that are not represented by the building-block tree.
	 * Linked-ancestor validation therefore reports this descendant conservatively instead of assuming it is safe.
	 */
	opaque?: boolean;

	/**
	 * Renders outside its model parent's DOM subtree. Validation exempts the portaled branch from nested-DOM
	 * conflicts, while the block remains a runtime event boundary according to `activation`.
	 */
	portal?: boolean;
};

export const INTERACTION_OWNER_ATTR = 'data-interaction-owner';

/**
 * Classify the rendered block itself, not its visual appearance or the actions of its children:
 * - use `always` when the block has its own click, keyboard, form, playback, drag, or gesture behavior;
 * - use `linked` when it is otherwise presentational and becomes actionable only with a resolved link;
 * - use `passive` when it only presents or groups content; actionable children are classified separately.
 *
 * For example, Video is `always` because its player owns playback interactions. Box is `linked`
 * because an unlinked layout Box has no action. CMS Repeater is `passive` because its wrapper only
 * renders repeated content; each repeated child keeps its own capability.
 */
export const BUILDING_BLOCK_CAPABILITIES: Record<ComponentTypes, BuildingBlockCapabilities> = {
	[COMPONENT_TYPE.TEXT]: { link: false, activation: 'passive' },
	[COMPONENT_TYPE.IMG]: { link: true, activation: 'linked' },
	[COMPONENT_TYPE.ICON]: { link: false, activation: 'passive' },
	[COMPONENT_TYPE.ANSWER]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.ANSWER_PROGRESS]: { link: false, activation: 'passive' },
	[COMPONENT_TYPE.AD_UNIT]: { link: false, activation: 'always', opaque: true },
	[COMPONENT_TYPE.FLOAT_ABOVE]: { link: false, activation: 'passive' },
	[COMPONENT_TYPE.FLOAT_BELOW]: { link: false, activation: 'passive' },
	[COMPONENT_TYPE.CONTENT]: { link: false, activation: 'passive' },
	[COMPONENT_TYPE.CARD]: { link: false, activation: 'passive' },
	[COMPONENT_TYPE.BUTTON]: { link: true, activation: 'always' },
	[COMPONENT_TYPE.BUTTON_SUBMIT]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.GOOGLE_LOGIN]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.SMS_LOGIN]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.BOX]: { link: true, activation: 'linked' },
	[COMPONENT_TYPE.SHAPE]: { link: false, activation: 'passive' },
	[COMPONENT_TYPE.EMBED]: { link: false, activation: 'always', opaque: true },
	[COMPONENT_TYPE.INFOGRAPHIC]: { link: false, activation: 'always', opaque: true },
	[COMPONENT_TYPE.TEXT_FIELD]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.TEXT_AREA_FIELD]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.CHECKBOX]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.SHARE]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.RESULT_TEXT]: { link: false, activation: 'passive' },
	[COMPONENT_TYPE.RESULT_SHAPE]: { link: false, activation: 'passive' },
	[COMPONENT_TYPE.SWIPE]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.VIDEO]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.TIMER]: { link: false, activation: 'passive' },
	[COMPONENT_TYPE.LOTTIE]: { link: false, activation: 'passive' },
	[COMPONENT_TYPE.SELECT]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.OVERLAY]: { link: false, activation: 'always', portal: true },
	[COMPONENT_TYPE.SORTABLE_BOX]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.COUNTER]: { link: false, activation: 'passive' },
	[COMPONENT_TYPE.ANSWER_INPUT]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.SLIDER]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.SLIDER_SLIDES]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.SLIDER_ARROW]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.SLIDER_PAGINATION]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.VISUAL_COMPARE]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.SEARCH_FIELD]: { link: false, activation: 'always' },
	[COMPONENT_TYPE.CMS_REPEATER]: { link: false, activation: 'passive' },
	[COMPONENT_TYPE.FILE_UPLOAD_FIELD]: { link: false, activation: 'always' },
};

const UNKNOWN_CAPABILITY: BuildingBlockCapabilities = { link: false, activation: 'always', opaque: true };

function getCapability(type: ComponentTypes): BuildingBlockCapabilities {
	// Persisted stories can outlive a deployment's type registry; unknown descendants are never assumed passive.
	return BUILDING_BLOCK_CAPABILITIES[type] ?? UNKNOWN_CAPABILITY;
}

/** Persisted LinkFields keys; Inspector allowlists and resolveLink share this list. */
export const LINK_FIELDS = [
	UNIFORM_PROPS.btnLink,
	UNIFORM_PROPS.btnTarget,
	UNIFORM_PROPS.btnNavLink,
	UNIFORM_PROPS.btnBlockLink,
	UNIFORM_PROPS.btnCollectionLink,
] as const;

export type LinkFields = Pick<BBUiConfig['componentProps'], (typeof LINK_FIELDS)[number]>;

export type ResolvedLink =
	| { kind: 'none' }
	| { kind: 'external'; href: string; target: '_blank' | '_self' }
	| { kind: 'internal'; cardId: string; blockId?: string; collectionItem?: string };

function configuredString(value: unknown): string | undefined {
	return typeof value === 'string' && value ? value : undefined;
}

export function resolveLink(fields: Partial<LinkFields>): ResolvedLink {
	const external = configuredString(fields[UNIFORM_PROPS.btnLink]);
	if (external) {
		return {
			kind: 'external',
			href: external,
			target: fields[UNIFORM_PROPS.btnTarget] ? '_blank' : '_self',
		};
	}

	const cardId = configuredString(fields[UNIFORM_PROPS.btnNavLink]);
	if (!cardId) return { kind: 'none' };

	const blockId = configuredString(fields[UNIFORM_PROPS.btnBlockLink]);
	const collectionItem = configuredString(fields[UNIFORM_PROPS.btnCollectionLink]);
	return {
		kind: 'internal',
		cardId,
		...(blockId ? { blockId } : {}),
		...(collectionItem ? { collectionItem } : {}),
	};
}

export function isLinkCapableType(type: ComponentTypes): boolean {
	return getCapability(type).link;
}

export function isLinked(block: Pick<BBModel, 'type' | 'uiConfig'>): boolean {
	return isLinkCapableType(block.type) && resolveLink(block.uiConfig.componentProps).kind !== 'none';
}

export function isIndependentAction(block: Pick<BBModel, 'type' | 'uiConfig'>): boolean {
	const capability = getCapability(block.type);
	return capability.activation === 'always' || (capability.activation === 'linked' && isLinked(block));
}

export function shouldInheritParentHover(block: Pick<BBModel, 'type' | 'uiConfig'>): boolean {
	return !isIndependentAction(block);
}

/**
 * Semantic Answer states continue to override descendants, while hover is owned by whichever
 * interactive region the pointer is actually over. Keeping the two provenances separate lets an
 * ancestor Answer remain hovered while a nested Button rejects that inherited value and uses its own.
 */
export function mergeBuildingBlockStates(
	localStates: Partial<Record<BBStates, boolean>>,
	parentStates?: Partial<Record<BBStates, boolean>>,
	{ inheritParentHover = true }: { inheritParentHover?: boolean } = {}
): Partial<Record<BBStates, boolean>> {
	if (!parentStates) return localStates;
	return {
		...localStates,
		...parentStates,
		[COMPONENT_STATES.HOVER]: Boolean(
			localStates[COMPONENT_STATES.HOVER] || (inheritParentHover && parentStates[COMPONENT_STATES.HOVER])
		),
	};
}

const ACTION_SELECTOR = [
	`[${INTERACTION_OWNER_ATTR}="true"]`,
	'a[href]',
	'button',
	'input',
	'select',
	'textarea',
	'[role="button"]',
	'[role="link"]',
	'[contenteditable="true"]',
].join(',');

export function findIndependentActionBoundary(target: EventTarget | null, owner: HTMLElement): HTMLElement | null {
	if (!(target instanceof Element)) return null;
	const boundary = target.closest<HTMLElement>(ACTION_SELECTOR);
	if (!boundary || boundary === owner) return null;
	return boundary;
}

export type StoryIssueCode =
	| 'interactive-descendant-conflict'
	| 'nested-inline-anchor'
	| 'missing-internal-card'
	| 'missing-internal-block'
	| 'opaque-descendant'
	| 'leaf-contains-elements';

export type StoryIssueElement = {
	elementId: string;
	nodeId?: string;
	name: string;
	type: ComponentTypes;
};

export type StoryIssueRepairContext = {
	ownerKind: 'element' | 'symbol-master';
	ancestors: StoryIssueElement[];
	misplacedChildren: StoryIssueElement[];
	symbolMasterId?: string;
};

type StoryIssueBase = {
	code: StoryIssueCode;
	severity: 'error' | 'warning';
	message: string;
	primaryElementId: string;
	primaryNodeId?: string;
	primaryElementName: string;
	primaryElementType: ComponentTypes;
	relatedElementIds?: string[];
	relatedElements?: StoryIssueElement[];
	context: Record<string, string>;
	repairContext?: StoryIssueRepairContext;
};

export type StoryIssue = StoryIssueBase &
	(
		| { location: 'card'; cardId: string; cardName: string }
		| {
				location: 'story';
				inspectionCardId?: string;
				inspectionCardName?: string;
		  }
	);

function hasInlineAnchor(block: BBModel): boolean {
	if (Array.isArray(block.children)) return false;
	return Object.values(block.children ?? {}).some(platforms =>
		Object.values(platforms ?? {}).some(value => typeof value === 'string' && /<a\b[^>]*href\s*=/i.test(value))
	);
}

type EffectiveBlock = { block: BBModel; path: string };

function collectEffectiveBlocks(
	elements: BBModel[],
	data: Pick<StoryVersionType['data'], 'elements' | 'symbols'>
): EffectiveBlock[] {
	const result: EffectiveBlock[] = [];
	componentWalkFull({
		elements,
		symbols: data.symbols ?? {},
		storyElements: data.elements,
		callback: ({ component, path }) => {
			if (component?._id && component.uiConfig?.componentProps) result.push({ block: component, path });
		},
	});
	return result;
}

function isDescendantPath(parentPath: string, candidatePath: string): boolean {
	return candidatePath.startsWith(`${parentPath}.children.`);
}

function getIssueElementDetails(block: BBModel) {
	return {
		elementId: block._id,
		nodeId: block.uiConfig.nodeProps.id,
		name: block.uiConfig.editorProps.name || block.type,
		type: block.type,
	};
}

function getIssueDisplayDetails(card: StoryVersionType['data']['steps'][number]['cards'][number], block: BBModel) {
	return {
		cardName: card.name,
		primaryElementName: block.uiConfig.editorProps.name || block.type,
		primaryElementType: block.type,
	};
}

export function validateStoryLinks(
	data: Pick<StoryVersionType['data'], 'elements' | 'steps' | 'symbols'>
): StoryIssue[] {
	const cards = data.steps.flatMap(step => step.cards);
	const cardById = new Map(cards.map(card => [card._id, card]));
	const effectiveBlocksByCard = new Map(
		cards.map(card => [card._id, collectEffectiveBlocks(card.elements, data)] as const)
	);
	const issues: StoryIssue[] = [];

	for (const card of cards) {
		const effectiveBlocks = effectiveBlocksByCard.get(card._id) ?? [];
		for (const { block, path } of effectiveBlocks) {
			const displayDetails = getIssueDisplayDetails(card, block);
			const link = resolveLink(block.uiConfig.componentProps);
			if (isLinkCapableType(block.type) && link.kind === 'internal') {
				const targetCard = cardById.get(link.cardId);
				if (!targetCard) {
					issues.push({
						code: 'missing-internal-card',
						severity: 'error',
						message: `Element "${block._id}" links to missing card "${link.cardId}".`,
						cardId: card._id,
						...displayDetails,
						location: 'card',
						primaryElementId: block._id,
						primaryNodeId: block.uiConfig.nodeProps.id,
						context: { targetCardId: link.cardId },
					});
				} else if (
					link.blockId &&
					!effectiveBlocksByCard
						.get(targetCard._id)
						?.some(({ block: target }) => target.uiConfig.nodeProps.id === link.blockId)
				) {
					issues.push({
						code: 'missing-internal-block',
						severity: 'error',
						message: `Element "${block._id}" links to missing block "${link.blockId}" in card "${link.cardId}".`,
						cardId: card._id,
						...displayDetails,
						location: 'card',
						primaryElementId: block._id,
						primaryNodeId: block.uiConfig.nodeProps.id,
						context: {
							targetCardId: link.cardId,
							targetCardName: targetCard.name,
							targetBlockId: link.blockId,
						},
					});
				}
			}

			if (!isLinked(block) || !Array.isArray(block.children)) continue;

			for (const { block: descendant, path: descendantPath } of effectiveBlocks) {
				if (!isDescendantPath(path, descendantPath)) continue;
				const hasPortalBoundary = effectiveBlocks.some(
					candidate =>
						candidate.path !== descendantPath &&
						isDescendantPath(path, candidate.path) &&
						isDescendantPath(candidate.path, descendantPath) &&
						getCapability(candidate.block.type).portal
				);
				if (hasPortalBoundary) continue;

				const capability = getCapability(descendant.type);
				if (capability.portal) continue;

				if (hasInlineAnchor(descendant)) {
					issues.push({
						code: 'nested-inline-anchor',
						severity: 'error',
						message: `Element "${block._id}" contains inline link "${descendant._id}".`,
						cardId: card._id,
						...displayDetails,
						location: 'card',
						primaryElementId: block._id,
						primaryNodeId: block.uiConfig.nodeProps.id,
						relatedElementIds: [descendant._id],
						relatedElements: [getIssueElementDetails(descendant)],
						context: { descendantType: descendant.type },
					});
					continue;
				}

				if (capability.opaque) {
					issues.push({
						code: 'opaque-descendant',
						severity: 'error',
						message: `Element "${block._id}" contains opaque interactive element "${descendant._id}".`,
						cardId: card._id,
						...displayDetails,
						location: 'card',
						primaryElementId: block._id,
						primaryNodeId: block.uiConfig.nodeProps.id,
						relatedElementIds: [descendant._id],
						relatedElements: [getIssueElementDetails(descendant)],
						context: { descendantType: descendant.type },
					});
					continue;
				}

				if (isIndependentAction(descendant)) {
					issues.push({
						code: 'interactive-descendant-conflict',
						severity: 'error',
						message: `Element "${block._id}" contains independent action "${descendant._id}".`,
						cardId: card._id,
						...displayDetails,
						location: 'card',
						primaryElementId: block._id,
						primaryNodeId: block.uiConfig.nodeProps.id,
						relatedElementIds: [descendant._id],
						relatedElements: [getIssueElementDetails(descendant)],
						context: { descendantType: descendant.type },
					});
				}
			}
		}
	}

	return issues;
}
